import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, actionKey } from '../../api';
import { ApiError } from '@shared/client';
import type { Statement } from '../../types';
import { Empty, ErrorBox, Field, Loading, RupeeInput, day, inr, rupeesToPaise, today, useBusy } from '../../ui';
import { billName, download } from './RelationshipParts';

interface InvoiceRef {
  _id: string; invoiceNumber: string; invoiceDate: string; amountPaise?: string;
  attachmentId?: string; filename?: string; statementId?: string; statementIds: string[];
}
const useInvoices = (relationshipId: string) => useQuery({ queryKey: ['invoices', relationshipId], queryFn: () => api.get<InvoiceRef[]>(`/api/relationships/${relationshipId}/invoice-refs`) });

function InvoiceFile({ invoice: r }: { invoice: InvoiceRef }) {
  return <div className="stack" style={{ gap: '.4rem' }}>
    <span><strong>Invoice {r.invoiceNumber}</strong> · {day(r.invoiceDate)}{r.amountPaise ? ` · ${inr(r.amountPaise)}` : ''}</span>
    {r.attachmentId && r.filename ? <button className="btn small" onClick={() => void download(`/api/attachments/${r.attachmentId}`, r.filename!)}>Download invoice {r.invoiceNumber}</button> : <small className="muted">Invoice number saved. The pump has not uploaded the file yet.</small>}
  </div>;
}

export function BillInvoices({ relationshipId, statementId }: { relationshipId: string; statementId: string }) {
  const q = useInvoices(relationshipId);
  if (q.isLoading) return <small>Loading invoices…</small>;
  if (q.error) return <ErrorBox error={q.error} />;
  const refs = q.data!.filter(r => r.statementIds.includes(statementId));
  return <div className="stack" style={{ gap: '.5rem', marginTop: '.5rem' }}>
    {refs.length ? refs.map(r => <InvoiceFile key={r._id} invoice={r} />) : <small className="muted">The pump has not attached an invoice to this bill yet.</small>}
  </div>;
}

export function InvoiceManager({ relationshipId }: { relationshipId: string }) {
  const q = useInvoices(relationshipId);
  const bills = useQuery({ queryKey: ['statements', relationshipId], queryFn: () => api.get<{ statements: Statement[] }>(`/api/relationships/${relationshipId}/statements`) });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<InvoiceRef | null>(null);
  const blank = () => ({ number: '', date: today(), amount: '', statementId: '' });
  const [f, setF] = useState(blank);
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const pending = useRef<{ key: ReturnType<typeof actionKey>; attachmentId?: string } | null>(null);
  const { busy, error, run } = useBusy();
  const latest = bills.data?.statements.filter(s => s.status === 'ISSUED') ?? [];
  const reset = () => { setEditing(null); setF(blank()); setFile(null); setFileKey(k => k + 1); setSubmitted(false); pending.current = null; };
  return <section className="card stack">
    <h2>GST invoices</h2>
    <p>Make the invoice in your billing software. Choose the matching bill below and upload its PDF or photo. The customer can download it from Bills. This does not add another charge.</p>
    {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
    {q.data && !q.data.length ? <Empty>No invoices added yet.</Empty> : null}
    <ul className="list">{q.data?.map(r => <li key={r._id} className="stack">
      <InvoiceFile invoice={r} />
      <small>{r.statementIds.length ? 'Linked to a bill' : 'Not linked to a bill yet'}</small>
      <button className="btn small" disabled={submitted || busy} onClick={() => { setEditing(r); setF({ number: r.invoiceNumber, date: r.invoiceDate, amount: '', statementId: latest.find(s => r.statementIds.includes(s._id))?._id ?? '' }); setFile(null); setFileKey(k => k + 1); }}>Choose bill / add file</button>
    </li>)}</ul>
    <h3>{editing ? `Update invoice ${editing.invoiceNumber}` : 'Add an invoice'}</h3>
    {bills.isLoading ? <Loading label="Loading bills…" /> : <ErrorBox error={bills.error} />}
    {bills.data && !latest.length ? <p className="alert info">Issue a monthly bill first, then attach its invoice here.</p> : null}
    <Field label="Bill for this invoice"><select value={f.statementId} disabled={submitted || busy} onChange={e => setF({ ...f, statementId: e.target.value })}>
      <option value="">Choose a bill…</option>{latest.map(s => <option key={s._id} value={s._id}>{billName(s.periodStart, s.periodEnd)} · {s.number}</option>)}
    </select></Field>
    {!editing ? <div className="grid three">
      <Field label="Invoice number"><input value={f.number} maxLength={60} disabled={submitted || busy} onChange={e => setF({ ...f, number: e.target.value })} /></Field>
      <Field label="Invoice date"><input type="date" value={f.date} disabled={submitted || busy} onChange={e => setF({ ...f, date: e.target.value })} /></Field>
      <Field label="Invoice amount (₹, optional)"><RupeeInput value={f.amount} disabled={submitted || busy} onChange={amount => setF({ ...f, amount })} /></Field>
    </div> : null}
    {!editing?.attachmentId ? <Field label="Invoice file (optional PDF/photo)"><input key={fileKey} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={submitted || busy} onChange={e => setFile(e.target.files?.[0] ?? null)} /></Field> : null}
    <ErrorBox error={error} />
    {submitted && error ? <p>Retry to finish saving this invoice. Your chosen details are kept to prevent a duplicate.</p> : null}
    <div className="row">
      <button className="btn primary" disabled={busy || !f.statementId || !f.number.trim() || !f.date || Boolean(f.amount && rupeesToPaise(f.amount) === null)} onClick={() => void run(async () => {
        try {
        setSubmitted(true);
        pending.current ??= { key: actionKey(`invoice:${relationshipId}:${editing?._id ?? f.number.trim()}:${f.statementId}`) };
        const p = pending.current;
        if (file && !p.attachmentId) {
          const fd = new FormData(); fd.append('purpose', 'INVOICE'); fd.append('file', file);
          p.attachmentId = (await api.post<{ id: string }>('/api/attachments', fd)).id;
        }
        const body = { statementId: f.statementId, attachmentId: p.attachmentId };
        if (editing) await api.post(`/api/invoice-refs/${editing._id}/link`, body, { idempotencyKey: p.key.key });
        else await api.post('/api/invoice-refs', { ...body, relationshipId, invoiceNumber: f.number.trim(), invoiceDate: f.date, amountPaise: rupeesToPaise(f.amount) ?? undefined }, { idempotencyKey: p.key.key });
        p.key.done(); reset(); await qc.invalidateQueries();
        } catch (err) {
          // A definite refusal is safe to edit. An uncertain network/server
          // response retains the exact upload and action key for a retry.
          if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 408) {
            pending.current?.key.done(); pending.current = null; setSubmitted(false);
          }
          throw err;
        }
      })}>{submitted && error ? 'Retry save' : 'Save invoice'}</button>
      {editing && !submitted ? <button className="btn" onClick={reset}>Cancel</button> : null}
    </div>
  </section>;
}
